// Projects: a folder of sources and media with a main file, as a desktop
// Blitz project is. Each project's details (name, main file, language mode)
// are kept in IndexedDB, and its files either in the browser's Origin
// Private File System (projects/<id>/) or, in Chromium browsers, in a folder
// on the user's disk that the File System Access API opened, edited in place.
// Both are FileSystemDirectoryHandles, so one code path serves both. Where
// neither is available, projects live in memory for the session.

const DATABASE = "b3d-ide", STORE = "projects";

const request = (operation) => new Promise((resolve, reject) => {
  operation.onsuccess = () => resolve(operation.result);
  operation.onerror = () => reject(operation.error);
});

async function openDatabase() {
  if (typeof indexedDB === "undefined") return null;
  const opening = indexedDB.open(DATABASE, 1);
  opening.onupgradeneeded = () => opening.result.createObjectStore(STORE, { keyPath: "id" });
  try {
    return await request(opening);
  } catch {
    return null;
  }
}

// A path's parts, with any "." or ".." refused: a project cannot hold files
// outside its folder.
export function splitPath(path) {
  const parts = path.replace(/\\/g, "/").split("/").filter((part) => part && part !== ".");
  if (!parts.length || parts.includes("..")) throw new Error(`"${path}" is not a path in the project`);
  return parts;
}

export const normalisePath = (path) => splitPath(path).join("/");

async function directoryFor(root, parts, create) {
  let directory = root;
  for (const part of parts) directory = await directory.getDirectoryHandle(part, { create });
  return directory;
}

// The files beneath a directory: [[path, File], ...], leaving out hidden
// files and folders (.git, .DS_Store).
async function listFiles(directory, prefix = "", into = []) {
  for await (const [name, handle] of directory.entries()) {
    if (name.startsWith(".")) continue;
    if (handle.kind === "directory") await listFiles(handle, prefix + name + "/", into);
    else into.push([prefix + name, await handle.getFile()]);
  }
  return into;
}

// Which file a folder of sources runs: the .bb file named after the folder,
// or else the only .bb file at the top, or else the first .bb file found, the
// shallowest first.
export function guessMainFile(paths, folderName = "") {
  const sources = paths.filter((path) => /\.bb$/i.test(path))
    .sort((a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b));
  const top = sources.filter((path) => !path.includes("/"));
  return top.find((path) => path.toLowerCase() === folderName.toLowerCase() + ".bb") ||
    (top.length === 1 ? top[0] : null) || sources[0] || null;
}

// The files a set of sources includes, by their paths in the project.
// files: [[path, Blob, Uint8Array or string], ...]
async function includedFiles(files) {
  const included = new Set();
  const decoder = new TextDecoder();
  for (const [path, contents] of files) {
    if (!/\.bb$/i.test(path)) continue;
    const text = typeof contents === "string" ? contents :
      contents instanceof Blob ? await contents.text() : decoder.decode(contents);
    const folder = path.includes("/") ? path.slice(0, path.lastIndexOf("/") + 1) : "";
    for (const match of text.matchAll(/^[ \t]*Include[ \t]+"([^"]+)"/gim)) {
      const parts = [];
      for (const part of (folder + match[1].replace(/\\/g, "/")).split("/")) {
        if (part === "..") parts.pop();
        else if (part && part !== ".") parts.push(part);
      }
      included.add(parts.join("/").toLowerCase());
    }
  }
  return included;
}

// Which file a folder of sources runs, as guessMainFile chooses, leaving out
// files that other files include, such as a shared start.bb.
export async function findMainFile(files, folderName = "") {
  const included = await includedFiles(files);
  const paths = files.map(([path]) => path);
  const candidates = paths.filter((path) => !included.has(path.toLowerCase()));
  return guessMainFile(candidates.some((path) => /\.bb$/i.test(path)) ? candidates : paths, folderName);
}

export class Project {
  constructor(store, meta, directory) {
    this.store = store;
    this.meta = { ...meta, mode: "extended" };
    this.directory = directory;       // null for a project held in memory
    this.files = new Map();           // path -> Blob (a File, or one written here)
    this.version = 0;                 // counts changes
    this.watchers = new Set();        // (path, Blob or null) for each file written or removed
  }
  // Calls watcher(path, blob) after each file is written, and with null
  // after one is removed, whoever changes it: the code editor, a tool, Blip
  // or a buddy session's room. Returns a function that stops it.
  watch(watcher) {
    this.watchers.add(watcher);
    return () => this.watchers.delete(watcher);
  }
  changed(path, blob) {
    for (const watcher of this.watchers) {
      try {
        watcher(path, blob);
      } catch (error) {
        console.warn(error);
      }
    }
  }

  get id() { return this.meta.id; }
  get name() { return this.meta.name; }
  get main() { return this.meta.main; }
  get isFolder() { return this.meta.kind === "folder"; }

  async load() {
    this.files = new Map(this.directory ? await listFiles(this.directory) : []);
    ++this.version;
  }

  paths() { return [...this.files.keys()].sort((a, b) => a.localeCompare(b)); }
  has(path) { return this.files.has(path); }
  blob(path) { return this.files.get(path); }
  async text(path) { return this.files.get(path).text(); }
  async bytes(path) { return new Uint8Array(await this.files.get(path).arrayBuffer()); }

  // Writes a file, making its folders.
  async write(path, contents) {
    const parts = splitPath(path);
    path = parts.join("/");
    const blob = contents instanceof Blob ? contents : new Blob([contents]);
    if (this.directory) {
      const folder = await directoryFor(this.directory, parts.slice(0, -1), true);
      const handle = await folder.getFileHandle(parts[parts.length - 1], { create: true });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
    }
    this.files.set(path, blob);
    ++this.version;
    await this.touch();
    this.changed(path, blob);
    return path;
  }

  async remove(path) {
    const parts = splitPath(path);
    if (this.directory) {
      const folder = await directoryFor(this.directory, parts.slice(0, -1), false);
      await folder.removeEntry(parts[parts.length - 1]);
    }
    this.files.delete(path);
    ++this.version;
    if (this.meta.main === path) await this.update({ main: guessMainFile(this.paths()) });
    else await this.touch();
    this.changed(path, null);
  }

  async rename(from, to) {
    to = normalisePath(to);
    if (from === to) return to;
    if (this.files.has(to)) throw new Error(`${to} already exists`);
    await this.write(to, this.files.get(from));
    await this.remove(from);
    if (this.meta.main === from || !this.meta.main) await this.update({ main: to });
    return to;
  }

  async update(changes) {
    Object.assign(this.meta, changes, { mode: "extended" });
    await this.touch();
  }

  async touch() {
    this.meta.updated = Date.now();
    await this.store.saveMeta(this.meta);
    if (this.store.changed) this.store.changed(this);
  }
}

export class ProjectStore {
  static async open() {
    const store = new ProjectStore();
    store.database = await openDatabase();
    try {
      const root = await navigator.storage.getDirectory();
      store.root = await root.getDirectoryHandle("projects", { create: true });
    } catch {
      store.root = null;
    }
    store.memory = new Map();         // id -> meta, without a database
    store.memoryFiles = new Map();    // id -> Map of files, without a file system
    return store;
  }

  // Whether projects outlast the page.
  get persistent() { return !!this.database && !!this.root; }
  // Whether folders on the user's disk can be opened in place.
  get canOpenFolders() { return typeof window.showDirectoryPicker === "function"; }

  async list() {
    const metas = this.database ?
      await request(this.database.transaction(STORE).objectStore(STORE).getAll()) : [...this.memory.values()];
    return metas.sort((a, b) => b.updated - a.updated);
  }

  async saveMeta(meta) {
    if (!this.database) {
      this.memory.set(meta.id, meta);
      return;
    }
    await request(this.database.transaction(STORE, "readwrite").objectStore(STORE).put(meta));
  }

  async meta(id) {
    if (!this.database) return this.memory.get(id) || null;
    return (await request(this.database.transaction(STORE).objectStore(STORE).get(id))) || null;
  }

  // A name no other project has: "Castle", then "Castle (2)".
  async uniqueName(name) {
    const taken = new Set((await this.list()).map((meta) => meta.name.toLowerCase()));
    if (!taken.has(name.toLowerCase())) return name;
    for (let n = 2; ; ++n) if (!taken.has(`${name} (${n})`.toLowerCase())) return `${name} (${n})`;
  }

  // Creates a project in the browser from files: [[path, contents], ...].
  // programs: for a sample, the language mode each of its programs needs.
  // shaders: the shader translations it comes with (account.js).
  // siteVersion: for an example, the version of the site it was copied from
  // (src/site.js).
  async create({ name, files, main = null, mode = "extended", example = null, programs = null, shaders = null,
    siteVersion = null }) {
    const id = crypto.randomUUID();
    const meta = { id, kind: "browser", name: await this.uniqueName(name), main, mode: "extended", example,
      created: Date.now(), updated: Date.now() };
    if (programs) meta.programs = programs;
    if (shaders) meta.shaders = shaders;
    if (siteVersion) meta.siteVersion = siteVersion;
    const directory = this.root ? await this.root.getDirectoryHandle(id, { create: true }) : null;
    const project = new Project(this, meta, directory);
    for (const [path, contents] of files) await project.write(path, contents);
    if (!project.meta.main) project.meta.main = await findMainFile(files, name);
    await project.touch();
    if (!directory) this.memoryFiles.set(id, project.files);
    return project;
  }

  // Adds a folder on the user's disk as a project, edited in place.
  async addFolder(handle) {
    const existing = (await this.list()).find((meta) => meta.kind === "folder" && meta.folderName === handle.name);
    for (const meta of existing ? [existing] : []) {
      if (meta.handle && await meta.handle.isSameEntry(handle)) return this.openProject(meta.id);
    }
    const meta = { id: crypto.randomUUID(), kind: "folder", name: await this.uniqueName(handle.name),
      folderName: handle.name, handle, main: null, mode: "extended", created: Date.now(), updated: Date.now() };
    const project = new Project(this, meta, handle);
    await project.load();
    meta.main = await findMainFile([...project.files], handle.name);
    await project.touch();
    return project;
  }

  // Opens a project. A folder on disk needs the user's permission again in
  // each session, which only a click can ask for: then this throws an error
  // whose needsPermission is true, and openProject(id, true) from a click
  // asks.
  async openProject(id, askPermission = false) {
    const meta = await this.meta(id);
    if (!meta) throw new Error("The project no longer exists");
    let directory = null;
    if (meta.kind === "folder") {
      directory = meta.handle;
      let permission = await directory.queryPermission({ mode: "readwrite" });
      if (permission === "prompt" && askPermission) permission = await directory.requestPermission({ mode: "readwrite" });
      if (permission !== "granted") {
        const error = new Error(`Allow access to the folder ${meta.folderName} to open ${meta.name}`);
        error.needsPermission = permission === "prompt";
        throw error;
      }
    } else if (this.root) {
      directory = await this.root.getDirectoryHandle(id, { create: true });
    }
    const project = new Project(this, meta, directory);
    if (directory) await project.load();
    else project.files = this.memoryFiles.get(id) || new Map();
    return project;
  }

  // Deletes a project. A folder on disk is only forgotten, never deleted.
  async remove(id) {
    const meta = await this.meta(id);
    if (meta && meta.kind === "browser" && this.root) {
      await this.root.removeEntry(id, { recursive: true }).catch(() => {});
    }
    this.memoryFiles.delete(id);
    if (this.database) await request(this.database.transaction(STORE, "readwrite").objectStore(STORE).delete(id));
    else this.memory.delete(id);
  }
}
